// react
import React from 'react';

// mui
import {
	Alert,
	Box,
	Chip,
	Divider,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	alpha,
	useTheme,
} from '@mui/material';
import IconAccountBalance from '@mui/icons-material/AccountBalanceWallet';

// types
import { Voucher } from '../../../@types';

// utils
import { formatCurrency } from '../../../utils/currency';
import { getVoucherTaxWithheld } from '../../../utils/voucher';

// Types

export interface VoucherPaymentReconciliationProps {
	/** Sibling vouchers from the same payment submission. */
	vouchers: Voucher[];
	/** Currency to reconcile each voucher to. */
	reportingCurrency: string;
	/**
	 * Optional map of spendPlanId → display name. When omitted the spend plan
	 * is shown as `Spend Plan #<id>`.
	 */
	spendPlanNames?: Record<number, string>;
	/**
	 * Optional title — defaults to "Payment Reconciliation". Pass null to hide.
	 */
	title?: string | null;
	/**
	 * Force render even when there's only one voucher in the reporting
	 * currency (the default behaviour skips that case).
	 */
	alwaysShow?: boolean;
	/** Optional label override for the chip next to the title. */
	reportingChipLabel?: string;
	/** Optional click handler — when provided, voucher rows become clickable. */
	onVoucherClick?: (voucher: Voucher) => void;
	/** When set, the row matching this voucher id gets a "This Voucher" chip. */
	highlightVoucherId?: number;
}

// Helpers

/**
 * Look up the rate for `currency` in the voucher's stored `exchangeRates` JSON.
 * Each entry's `exchangeRate` represents:
 *   1 unit of `entry.currency` = `entry.exchangeRate` units of the base
 *   (reporting) currency.
 *
 * Returns null when no rate is captured for that currency (e.g. the voucher
 * was created before FX capture was wired, or the user skipped the row).
 */
const findRateForCurrency = (
	voucher: Voucher,
	currency: string
): { rate: number; date: string | null } | null => {
	const list = voucher.exchangeRates || [];
	const match = list.find((er) => er.currency === currency);
	if (!match || !match.exchangeRate) return null;
	return { rate: Number(match.exchangeRate), date: match.dateExchangeRate || null };
};

/**
 * Convert a voucher's `amount` (or any field on it) into `targetCurrency`,
 * using the rates captured on the voucher. Rates are stored relative to the
 * project reporting currency so we apply: native * sourceRate / targetRate.
 *
 * Returns null when the voucher lacks a rate for its native currency.
 */
export const convertVoucherAmount = (
	voucher: Voucher,
	value: number,
	targetCurrency: string
): number | null => {
	const nativeCcy = voucher.currency || targetCurrency;
	if (nativeCcy === targetCurrency) return value;
	const source = findRateForCurrency(voucher, nativeCcy);
	const target = findRateForCurrency(voucher, targetCurrency);
	const targetRate = target?.rate ?? 1;
	if (!source || !targetRate) return null;
	return (value * source.rate) / targetRate;
};

// Component

export const VoucherPaymentReconciliation: React.FC<VoucherPaymentReconciliationProps> = ({
	vouchers,
	reportingCurrency,
	spendPlanNames,
	title = 'Payment Reconciliation',
	alwaysShow = false,
	reportingChipLabel,
	onVoucherClick,
	highlightVoucherId,
}) => {
	const theme = useTheme();

	if (!vouchers || vouchers.length === 0) return null;

	// Only worth showing when there's something to reconcile: either >1 voucher
	// in the group, or any voucher denominated in a non-reporting currency.
	const distinctCurrencies = new Set(vouchers.map((v) => v.currency || reportingCurrency));
	const hasNonReporting = vouchers.some(
		(v) => (v.currency || reportingCurrency) !== reportingCurrency
	);
	if (!alwaysShow && vouchers.length < 2 && !hasNonReporting) return null;

	// Per-voucher reconciliation rows.
	const rows = vouchers.map((v) => {
		const native = Number(v.amount || 0);
		const nativeCcy = v.currency || reportingCurrency;
		const taxNative = getVoucherTaxWithheld(v);
		const netNative = native - taxNative;

		let rate: number | null = null;
		let rateDate: string | null = null;
		let reportingEquivalent: number | null = null;
		let reportingTax: number | null = null;
		let reportingNet: number | null = null;
		let missingRate = false;

		if (nativeCcy === reportingCurrency) {
			rate = 1;
			reportingEquivalent = native;
			reportingTax = taxNative;
			reportingNet = netNative;
		} else {
			// Rates in voucher.exchangeRates are denominated against the project
			// reporting currency: 1 unit of entry.currency = entry.exchangeRate
			// units of reporting. To convert into an arbitrary `reportingCurrency`
			// (which may itself differ from the project reporting currency, e.g.
			// when reconciling into a spend plan's account currency) we compute:
			//   native * sourceRate / targetRate
			// where targetRate defaults to 1 when the target IS the project
			// reporting currency (and therefore not present in the rates list).
			const source = findRateForCurrency(v, nativeCcy);
			const target = findRateForCurrency(v, reportingCurrency);
			const targetRate = target?.rate ?? 1;
			if (source && targetRate) {
				rate = source.rate / targetRate;
				rateDate = source.date;
				reportingEquivalent = (native * source.rate) / targetRate;
				reportingTax = (taxNative * source.rate) / targetRate;
				reportingNet = (netNative * source.rate) / targetRate;
			} else {
				missingRate = true;
			}
		}

		return {
			id: v.id,
			spendPlanId: v.spendPlanId,
			spendPlanLabel:
				v.spendPlanId && spendPlanNames?.[v.spendPlanId]
					? spendPlanNames[v.spendPlanId]
					: v.spendPlanId
						? `Spend Plan #${v.spendPlanId}`
						: '—',
			native,
			nativeCcy,
			taxNative,
			netNative,
			rate,
			rateDate,
			reportingEquivalent,
			reportingTax,
			reportingNet,
			missingRate,
		};
	});

	const totalReporting = rows.reduce((s, r) => s + (r.reportingEquivalent ?? 0), 0);
	const totalReportingTax = rows.reduce((s, r) => s + (r.reportingTax ?? 0), 0);
	const totalReportingNet = rows.reduce((s, r) => s + (r.reportingNet ?? 0), 0);
	const anyMissing = rows.some((r) => r.missingRate);

	return (
		<Box
			sx={{
				border: '1px solid',
				borderColor: alpha(theme.palette.primary.main, 0.2),
				borderRadius: 1.5,
				bgcolor: alpha(theme.palette.primary.main, 0.03),
				p: 2,
			}}>
			{title && (
				<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
					<IconAccountBalance sx={{ fontSize: 18, color: theme.palette.primary.main }} />
					<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
						{title}
					</Typography>
					<Chip
						size="small"
						label={reportingChipLabel ?? `Reporting: ${reportingCurrency}`}
						sx={{ height: 20, fontSize: '0.7rem' }}
					/>
					{distinctCurrencies.size > 1 && (
						<Chip
							size="small"
							color="warning"
							label={`${distinctCurrencies.size} currencies`}
							sx={{ height: 20, fontSize: '0.7rem' }}
						/>
					)}
				</Stack>
			)}

			<TableContainer>
				<Table size="small" sx={{ '& td, & th': { px: 1, py: 0.75 } }}>
					<TableHead>
						<TableRow>
							<TableCell>Voucher</TableCell>
							<TableCell>Source</TableCell>
							<TableCell align="right">Amount (native)</TableCell>
							<TableCell align="right">Tax</TableCell>
							<TableCell align="right">FX → {reportingCurrency}</TableCell>
							<TableCell align="right">Net ({reportingCurrency})</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.map((r) => {
							const voucher = vouchers.find((v) => v.id === r.id);
							if (!voucher) return null;
							const clickable = !!onVoucherClick;
							return (
								<TableRow
									key={r.id}
									hover
									onClick={clickable && onVoucherClick ? () => onVoucherClick(voucher) : undefined}
									sx={clickable ? { cursor: 'pointer' } : undefined}>
									<TableCell sx={{ fontWeight: 600 }}>
										<Stack direction="row" spacing={0.75} alignItems="center">
											<span>#{r.id}</span>
											{highlightVoucherId === r.id && (
												<Chip
													size="small"
													color="primary"
													label="This Voucher"
													sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
												/>
											)}
										</Stack>
									</TableCell>
									<TableCell>
										<Typography variant="body2">{r.spendPlanLabel}</Typography>
									</TableCell>
									<TableCell align="right">
										{formatCurrency({ value: r.native, currency: r.nativeCcy })}
									</TableCell>
									<TableCell align="right">
										{r.taxNative > 0
											? formatCurrency({ value: r.taxNative, currency: r.nativeCcy })
											: '—'}
									</TableCell>
									<TableCell align="right">
										{r.missingRate ? (
											<Chip size="small" color="warning" label="rate missing" sx={{ height: 18 }} />
										) : r.nativeCcy === reportingCurrency ? (
											<Typography variant="caption" color="text.secondary">
												—
											</Typography>
										) : (
											<Stack alignItems="flex-end">
												<Typography variant="body2">× {r.rate?.toFixed(4)}</Typography>
												{r.rateDate && (
													<Typography variant="caption" color="text.secondary">
														{r.rateDate}
													</Typography>
												)}
											</Stack>
										)}
									</TableCell>
									<TableCell align="right" sx={{ fontWeight: 600 }}>
										{r.reportingNet != null
											? formatCurrency({ value: r.reportingNet, currency: reportingCurrency })
											: '—'}
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>

			<Divider sx={{ my: 1.5 }} />

			<Stack spacing={0.5}>
				<Stack direction="row" justifyContent="space-between">
					<Typography variant="body2" color="text.secondary">
						Gross total
					</Typography>
					<Typography variant="body2" sx={{ fontWeight: 600 }}>
						{formatCurrency({ value: totalReporting, currency: reportingCurrency })}
					</Typography>
				</Stack>
				{totalReportingTax > 0 && (
					<Stack direction="row" justifyContent="space-between">
						<Typography variant="body2" color="text.secondary">
							Tax withheld
						</Typography>
						<Typography variant="body2" sx={{ fontWeight: 500, color: 'error.main' }}>
							{'\u2212'} {formatCurrency({ value: totalReportingTax, currency: reportingCurrency })}
						</Typography>
					</Stack>
				)}
				<Stack direction="row" justifyContent="space-between">
					<Typography variant="body2" sx={{ fontWeight: 700 }}>
						Net disbursement
					</Typography>
					<Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
						{formatCurrency({ value: totalReportingNet, currency: reportingCurrency })}
					</Typography>
				</Stack>
			</Stack>

			{anyMissing && (
				<Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>
					One or more vouchers are missing an exchange rate for their source currency. Reporting
					totals exclude those rows.
				</Alert>
			)}
		</Box>
	);
};

export default VoucherPaymentReconciliation;
